import fetchInstructionListsForPackages from './fetchInstructionListsForPackages';
import summarizeJobWorkInstructions from './summarizeJobWorkInstructions';

const normalizePackageId = (value) => {
  if (typeof value === 'string' || typeof value === 'number') {
    const normalized = String(value).trim();
    return normalized || null;
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value._id || value.id;
  if (typeof candidate === 'string' || typeof candidate === 'number') {
    const normalized = String(candidate).trim();
    return normalized || null;
  }

  return null;
};

export default async function loadJobWorkInstructionStatus(
  client,
  jobId,
  linkedPackageRefs = []
) {
  const packagesResponse = await client.get(
    `/work-instructions-package/job/${jobId}`
  );
  const packages = Array.isArray(packagesResponse.data)
    ? packagesResponse.data
    : [];

  const packageById = new Map(
    packages
      .map((wiPackage) => [normalizePackageId(wiPackage), wiPackage])
      .filter(([packageId]) => Boolean(packageId))
  );

  const normalizedLinkedPackageIds = [];

  (Array.isArray(linkedPackageRefs) ? linkedPackageRefs : []).forEach(
    (linkedPackageRef) => {
      const packageId = normalizePackageId(linkedPackageRef);
      if (packageId) {
        normalizedLinkedPackageIds.push(packageId);
      }

      if (!packageId || packageById.has(packageId)) {
        return;
      }

      packageById.set(packageId, { _id: packageId, id: packageId });
    }
  );

  const allRelevantPackages = Array.from(packageById.values());

  if (allRelevantPackages.length === 0) {
    return {
      hasInstructions: false,
      instructionCount: 0,
    };
  }

  const instructionLists = await fetchInstructionListsForPackages(
    client,
    allRelevantPackages
  );

  const packageSummary = summarizeJobWorkInstructions(instructionLists);
  if (packageSummary.instructionCount > 0) {
    return packageSummary;
  }

  if (normalizedLinkedPackageIds.length < 1) {
    return packageSummary;
  }

  // Product templates can expose instructions via linked WIP ids that are not
  // returned by the job package endpoint in all lifecycle/timing scenarios.
  try {
    const linkedInstructionsResponse = await client.get(
      `/work-instructions/product-template/${jobId}`
    );
    const linkedInstructions = Array.isArray(linkedInstructionsResponse?.data)
      ? linkedInstructionsResponse.data
      : [];

    return {
      hasInstructions: linkedInstructions.length > 0,
      instructionCount: linkedInstructions.length,
    };
  } catch (_error) {
    return packageSummary;
  }
}
